import { Tag } from '@carbon/react';
import type { JSONValue } from 'lib/types';
import { Bullhorn } from 'components/icons';
import { ActivityHeader } from 'components/activity-header';
import type { VersionActivityItem } from './types';
import style from './activity-item.module.scss';
import { isISOTimestamp, isDateString, parseLocalDateTime } from 'utils/dates';
import { pascaleCaseToTitleCase } from 'utils/strings';
import { PlainLink } from 'components/link';

const formatValue = (value: JSONValue): string => {
  if (value === null || value === undefined) {
    return '--';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' && value.length === 0) {
    return '--';
  }

  if (typeof value === 'string' && isISOTimestamp(value)) {
    return parseLocalDateTime(value, true);
  }

  if (typeof value === 'string' && isDateString(value)) {
    return parseLocalDateTime(value);
  }

  return String(value);
};

const IGNORED_FIELDS = ['updated_at', 'created_at'];

const filterChanges = (
  changes: Record<string, [JSONValue, JSONValue]>
): Record<string, [JSONValue, JSONValue]> => {
  return Object.fromEntries(
    Object.entries(changes).filter(([key]) => !IGNORED_FIELDS.includes(key))
  );
};

interface VersionActivityProps {
  item: VersionActivityItem;
}

export const VersionActivity = ({ item }: VersionActivityProps) => {
  const { data, itemType, itemId, itemHref, createdAt, actionTag } = item;
  const { event, whodunnit, objectChanges } = data;

  const filteredChanges = filterChanges(objectChanges);
  const changeEntries = Object.entries(filteredChanges);

  const actionLabel =
    event === 'create'
      ? 'Created'
      : event === 'destroy'
        ? 'Deleted'
        : 'Updated';

  return (
    <li className={style.activityItem}>
      <ActivityHeader
        Icon={Bullhorn}
        createdBy={whodunnit ?? 'System'}
        createdAt={createdAt}
      />

      <div className={style.content}>
        {actionTag && (
          <Tag type={actionTag.color} size="sm">
            {actionTag.label}
          </Tag>
        )}
        <div className={style.actionLabel}>
          {actionLabel} {pascaleCaseToTitleCase(itemType)}
          {itemHref && (
            <>
              {' '}
              <PlainLink href={itemHref} openInNewTab>
                {itemId}
              </PlainLink>
            </>
          )}
        </div>

        {changeEntries.length > 0 && (
          <ul className={style.changesList}>
            {changeEntries.map(([field, [oldValue, newValue]]) => {
              const showNewValue =
                event === 'create' ||
                (event === 'destroy' && field === 'deleted_at');
              const showOldValue =
                event === 'destroy' && field !== 'deleted_at';

              return (
                <li key={field} className={style.changeItem}>
                  <span className={style.fieldName}>{field}:</span>
                  {showNewValue ? (
                    <span>{formatValue(newValue)}</span>
                  ) : showOldValue ? (
                    <span>{formatValue(oldValue)}</span>
                  ) : (
                    <>
                      <span>{formatValue(oldValue)}</span>
                      <span className={style.arrow}>→</span>
                      <span>{formatValue(newValue)}</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </li>
  );
};
